import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
	Box,
	Paper,
	Button,
	Typography,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableFooter,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	InputAdornment,
	IconButton,
	Chip,
	Grid2,
	Card,
	CardContent,
	Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { Iconify } from '../Minimal/src/components/iconify';
import { TablePaginationCustom } from '../Minimal/src/components/pagination';
import { useTable } from '../Minimal/src/hooks/use-table';
import Header from '../Header';
import Footer from '../Footer';
import config from "../common/Config";
import DayClose from "../sales/Day_Close";
import OpeningBalance from "../sales/Opening_Balance";
import { toast } from 'sonner';
import Finish_Pay_Incomplete_Order from "./Finish_Pay_Incomplete_Order";
import { v4 as uuidv4 } from 'uuid';
import printPaymentReceipt from './printPaymentReceipt';
import "../common/commonStyles.css";

const isRhpPosListUser = () => {
	if (config.PREFIX === 'rhp') {
		return true;
	}
	const token = String(localStorage.getItem('accessToken') || '').toLowerCase();
	const role = String(localStorage.getItem('user_role') || '').trim();
	return token === 'rhp' || token === 'hp' || role === '18' || role === '19';
};

const IncompleteOrderPosList = () => {
	const accessToken = localStorage.getItem('accessToken');
	const userId = localStorage.getItem('userId');
	const user_role = useMemo(() => localStorage.getItem('user_role'), []);
	const isRhpUser = useMemo(() => isRhpPosListUser(), []);
	const pageTitle = isRhpUser ? 'Order List' : 'Incomplete Order List';
	const tableColSpan = useMemo(() => {
		let cols = 6;
		if (!isRhpUser) {
			cols += 2;
		}
		if (user_role !== '18') {
			cols += 1;
		}
		return cols;
	}, [isRhpUser, user_role]);
	const [orders, setOrders] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [totalRecords, setTotalRecords] = useState(0);

	const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
	const [payDate, setPayDate] = useState(new Date());
	const [activePaymentMethod, setActivePaymentMethod] = useState('cash');
	const [payments, setPayments] = useState([]);
	const [amount, setAmount] = useState('');
	const [chequeNo, setChequeNo] = useState('');
	const [chequeDate, setChequeDate] = useState('');
	const [cardNumber, setCardNumber] = useState('');
	const [voucherNo, setVoucherNo] = useState('');
	const [voucherExpiryDate, setVoucherExpiryDate] = useState('');
	const [paidAmt, setPaidAmt] = useState(0.00);
	const [selectedOrder, setSelectedOrder] = useState(null);
	const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);
	const paymentSubmitInFlightRef = useRef(false);
	const currencySymbol = localStorage.getItem("currencySymbol") || '£';

	const table = useTable({
		defaultRowsPerPage: 50,
		defaultDense: false,
		defaultOrder: "desc",
		defaultOrderBy: "date",
		defaultSelected: [],
		defaultCurrentPage: 0,
	});

	const fetchOrders = async () => {
		try {
			setIsLoading(true);
			const formData = new FormData();
			formData.append('draw', 1);
			formData.append('start', table.page * table.rowsPerPage);
			formData.append('length', table.rowsPerPage);
			formData.append('search[value]', searchQuery || '');
			formData.append('searchValue', searchQuery || '');
			formData.append('sale_status', 'Incomplete');
			formData.append('user_id', userId);

			for (let i = 0; i < 9; i++) {
				formData.append(`columns[${i}][data]`, i.toString());
				formData.append(`columns[${i}][name]`, '');
				formData.append(`columns[${i}][searchable]`, 'true');
				formData.append(`columns[${i}][orderable]`, 'false');
				formData.append(`columns[${i}][search][value]`, '');
				formData.append(`columns[${i}][search][regex]`, 'false');
			}

			formData.append('order[0][column]', '0');
			formData.append('order[0][dir]', 'desc');
			formData.append('search[regex]', 'false');

			console.log('Fetching orders with params:', {
				draw: 1,
				start: table.page * table.rowsPerPage,
				length: table.rowsPerPage,
				searchValue: searchQuery || '',
				sale_status: 'Incomplete',
				user_id: userId
			});

			const response = await fetch(`${config.API_URL}/sales/pos_list`, {
				method: 'POST',
				headers: {
					authorization: `Bearer ${accessToken}`,
				},
				body: formData,
			});

			if (!response.ok) {
				const errorText = await response.text();
				console.error('Server error response:', errorText);
				throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
			}

			const data = await response.json();
			console.log('Received data:', data);

			if (data && Array.isArray(data.data)) {
				setOrders(data.data);
				setTotalRecords(data.recordsTotal || data.data.length);
			} else {
				setOrders([]);
				setTotalRecords(0);
			}
		} catch (error) {
			console.error('Error fetching orders:', error);
			toast.error(`Failed to load orders: ${error.message}`);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (accessToken === null) {
			window.location.href = `${config.BASE_URL}/login`;
			return;
		}
		fetchOrders();
	}, [table.page, table.rowsPerPage, searchQuery]);

	useEffect(() => {
		const openingBalance = async () => {
			const formData = new FormData();
			formData.append("user_id", userId);
			try {
				const response = await fetch(`${config.API_URL}/ws_get_opening_bal`, {
					method: "post",
					headers: {
						authorization: `Bearer ${accessToken}`,
					},
					body: formData,
				});

				if (!response.ok) {
					throw new Error(`Error: ${response.statusText}`);
				}
				const data = await response.json();

				if (data && data.success) {
				} else {
					openModal();
				}
				return data;
			} catch (error) {
				console.error("Error fetching opening balance:", error);
			}
		};

		const openModal = () => {
			const myModal = new window.bootstrap.Modal(
				document.getElementById("opening_balance")
			);
			myModal.show();
		};

		const handleKeyPress = (event) => {
			if (event.ctrlKey && event.key === 'd') {
				event.preventDefault();
				const modal = new window.bootstrap.Modal(document.getElementById('Day-Close'));
				modal.show();
			}
		};

		window.addEventListener('keydown', handleKeyPress);
		openingBalance();

		return () => {
			window.removeEventListener('keydown', handleKeyPress);
		};
	}, []);

	const handlePayClick = (order) => {
		setSelectedOrder(order);
		setPayDate(new Date());
		setActivePaymentMethod('cash');
		setPayments([]);
		setAmount('');
		setChequeNo('');
		setChequeDate('');
		setCardNumber('');
		setVoucherNo('');
		setVoucherExpiryDate('');
		setPaidAmt(0.00);
		setPaymentDialogOpen(true);
	};

	const resetInputs = () => {
		setAmount('');
		setChequeNo('');
		setChequeDate('');
		setCardNumber('');
		setVoucherNo('');
		setVoucherExpiryDate('');
	};

	const addPaymentWithMethod = (method) => {
		if (method === 'creditNote') {
			setActivePaymentMethod(method);
			return;
		}

		if (method === 'cheque' || method === 'voucher') {
			if (activePaymentMethod !== method) {
				setActivePaymentMethod(method);
				return;
			}
		} else {
			setActivePaymentMethod(method);
		}

		const currentBalance = Math.max(0, parseFloat(selectedOrder?.grand_total || 0) - paidAmt).toFixed(2);
		if (!amount || parseFloat(amount) < 0.01) {
			if (method === 'cash') {
				toast.error('Please enter a valid amount greater than zero!');
			} else if (method === 'card') {
				setActivePaymentMethod('card');
			}
			return;
		}
		if (parseFloat(amount) > parseFloat(currentBalance)) {
			toast.error('Amount cannot be greater than balance!');
			return;
		}
		if (method === 'cheque' && !chequeNo) {
			toast.error('Please enter cheque no');
			return;
		}
		if (method === 'voucher' && !voucherNo) {
			toast.error('Please enter voucher no');
			return;
		}
		if (method === 'voucher' && !voucherExpiryDate) {
			toast.error('Please enter voucher expiry date');
			return;
		}

		const newPaidAmt = parseFloat(paidAmt) + parseFloat(amount || 0);
		setPaidAmt(newPaidAmt);
		const newPayment = {
			uid: uuidv4(),
			amount,
			method,
			chequeNo: method === 'cheque' ? chequeNo : '',
			chequeDate: method === 'cheque' ? chequeDate : '',
			cardNumber: method === 'card' ? cardNumber : '',
			voucherNo: method === 'voucher' ? voucherNo : '',
			voucherExpiryDate: method === 'voucher' ? voucherExpiryDate : '',
		};

		setPayments((prev) => [...prev, newPayment]);
		resetInputs();
		setActivePaymentMethod(method === 'cash' ? 'cash' : method);
	};

	const handleAmountCancel = (id) => {
		const paymentToDelete = payments.find(payment => payment.uid === id);
		if (paymentToDelete) {
			const newPaidAmt = parseFloat(paidAmt) - parseFloat(paymentToDelete.amount || 0);
			setPaidAmt(newPaidAmt);
		}
		setPayments(payments.filter(payment => payment.uid !== id));
	};

	const clearPaymentData = () => {
		resetInputs();
		setPayments([]);
		setPaidAmt(0.00);
		setActivePaymentMethod('cash');
	};

	const handlePaymentSubmit = async () => {
		if (paymentSubmitInFlightRef.current) return;
		if (activePaymentMethod === 'creditNote') {
			if (!selectedOrder?.credit_type_name || selectedOrder?.credit_type_name === 'undefined') {
				toast.error('No credit facility available');
				return;
			}
		} else {
			if (paidAmt < parseFloat(selectedOrder?.grand_total || 0)) {
				toast.error('Total paid amount must be equal to or greater than invoice total');
				return;
			}
		}

		const formatDate = (date) => {
			const d = new Date(date);
			const year = d.getFullYear();
			const month = String(d.getMonth() + 1).padStart(2, '0');
			const day = String(d.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		};

		let cashAmount = 0, chequeAmount = 0, cardAmount = 0, voucherAmount = 0;
		let firstChequeNo = '', firstChequeDate = '', firstCardNumber = '', firstVoucherNo = '', firstVoucherExpiry = '';
		payments.forEach(p => {
			if (p.method === 'cash') cashAmount += parseFloat(p.amount || 0);
			if (p.method === 'cheque') {
				chequeAmount += parseFloat(p.amount || 0);
				if (!firstChequeNo && p.chequeNo) firstChequeNo = p.chequeNo;
				if (!firstChequeDate && p.chequeDate) firstChequeDate = p.chequeDate;
			}
			if (p.method === 'card') {
				cardAmount += parseFloat(p.amount || 0);
				if (!firstCardNumber && p.cardNumber) firstCardNumber = p.cardNumber;
			}
			if (p.method === 'voucher') {
				voucherAmount += parseFloat(p.amount || 0);
				if (!firstVoucherNo && p.voucherNo) firstVoucherNo = p.voucherNo;
				if (!firstVoucherExpiry && p.voucherExpiryDate) firstVoucherExpiry = p.voucherExpiryDate;
			}
		});

		const formData = new FormData();
		formData.append('user_id', userId);
		formData.append('customer_id', selectedOrder?.customer_id || '');
		formData.append('date', formatDate(payDate));
		formData.append('amount', activePaymentMethod === 'creditNote' ? 0 : paidAmt);
		formData.append('paid_by', activePaymentMethod === 'creditNote' ? 'onAccount' : activePaymentMethod);
		formData.append('sale_id', selectedOrder?.ids || '');

		formData.append('cheque_no', firstChequeNo);
		formData.append('cheque_date', firstChequeDate);
		formData.append('card_number', firstCardNumber);
		formData.append('voucher_no', firstVoucherNo);
		formData.append('voucher_expiry_date', firstVoucherExpiry);

		formData.append('cash_amount', cashAmount.toFixed(2));
		formData.append('cheque_amount', chequeAmount.toFixed(2));
		formData.append('card_amount', cardAmount.toFixed(2));
		formData.append('voucher_amount', voucherAmount.toFixed(2));

		formData.append('payments', JSON.stringify(payments));

		paymentSubmitInFlightRef.current = true;
		setIsPaymentSubmitting(true);
		try {
			const response = await fetch(`${config.API_URL}/pos_payment`, {
				method: 'POST',
				headers: {
					authorization: `Bearer ${accessToken}`,
				},
				body: formData,
			});

			if (!response.ok) {
				throw new Error('Failed to add payment');
			}

			const data = await response.json();

			if (data.success) {
				toast.success('Payment added successfully');
				if (!isRhpUser) {
					try {
						await printPaymentReceipt(selectedOrder?.ids, payments, selectedOrder?.grand_total, paidAmt);
					} catch (error) {
						console.error('Error generating payment receipt:', error);
					}
				}
				setPaymentDialogOpen(false);
				clearPaymentData();
				fetchOrders();
			} else {
				toast.error(data.message || 'Failed to add payment');
			}
		} catch (error) {
			console.error('Error adding payment:', error);
			toast.error('Failed to add payment');
		} finally {
			paymentSubmitInFlightRef.current = false;
			setIsPaymentSubmitting(false);
		}
	};

	const filteredOrders = useMemo(() => {
		if (!searchQuery) return orders;
		const query = searchQuery.toLowerCase();
		return orders.filter(order =>
			(order.reference_no && order.reference_no.toLowerCase().includes(query)) ||
			(order.customer && order.customer.toLowerCase().includes(query)) ||
			(order.date && order.date.toLowerCase().includes(query))
		);
	}, [orders, searchQuery]);

	const formatCurrency = (value) => {
		const symbol = localStorage.getItem("currencySymbol") || '£';
		return `${symbol}${parseFloat(value || 0).toFixed(2)}`;
	};

	const balance = Math.max(0, parseFloat(selectedOrder?.grand_total || 0) - paidAmt).toFixed(2);

	return (
		<>
			<DayClose />
			{user_role !== '18' ? (<OpeningBalance />) : null}
			<Finish_Pay_Incomplete_Order
				orderId={selectedOrder?.ids}
				grandTotal={selectedOrder?.grand_total}
				current_Bal={selectedOrder?.current_bal}
				credit_Type_Name={selectedOrder?.credit_type_name}
				prev_Due={selectedOrder?.prev_due}
				fetchEposSalesTable={fetchOrders}
			/>

			<Box className="page-container" >
				<Paper
					className="paper-style"
				>
					<Box sx={{ px: 2, py: 1.2 }}>
						<Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Typography className="page-title">
									{pageTitle}
								</Typography>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 4 }}>
								<TextField
									className="input-border"
									placeholder={isRhpUser ? 'Search by order no, customer...' : 'Search by reference, customer...'}
									size="small"
									fullWidth
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									slotProps={{
										input: {
											startAdornment: (
												<InputAdornment position="start">
													<Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
												</InputAdornment>
											),
											endAdornment: searchQuery && (
												<InputAdornment position="end">
													<IconButton
														size="small"
														onClick={() => setSearchQuery("")}
														sx={{ color: "#637381" }}
													>
														<Iconify icon="eva:close-fill" width={18} />
													</IconButton>
												</InputAdornment>
											),
										}
									}}
									sx={{
										"& .MuiOutlinedInput-root": {
											backgroundColor: "#f5f7fa",
											"&:hover": {
												backgroundColor: "#fff"
											},
											"&.Mui-focused": {
												backgroundColor: "#fff"
											}
										}
									}}
								/>
							</Grid2>
							{user_role !== '18' ? (
								<Grid2 size={{ xs: 12, md: 2 }} display="flex" justifyContent="flex-end" alignItems="center">
									<Button
										variant="contained"

										fullWidth
										data-bs-toggle="modal"
										data-bs-target="#Day-Close"
										startIcon={<Iconify icon="solar:calendar-mark-bold" width={20} />}
										sx={{
											textTransform: "none",
											fontWeight: 500,
											boxShadow: "none",
											backgroundColor: "#F8991D",
											'&:hover': {
												backgroundColor: "#F8991D",
												boxShadow: "none",
												color: "#fff"
											}
										}}
									>
										Day Close
									</Button>
								</Grid2>
							) : null}
						</Grid2>
					</Box>
					<TableContainer>
						<Table
							size="medium"
							className="table-cell-border"
						>
							<TableHead>
								<TableRow className="table-cell-header">
									<TableCell sx={{ width: "120px" }}>Date</TableCell>
									<TableCell sx={{ width: "120px" }}>{isRhpUser ? 'Order No' : 'Reference No'}</TableCell>
									<TableCell sx={{ width: "250px" }}>{isRhpUser ? 'Customer (A/C)' : 'Customer'}</TableCell>
									<TableCell align="right" sx={{ width: "120px" }}>Grand Total</TableCell>
									<TableCell align="right" sx={{ width: "120px" }}>Paid</TableCell>
									<TableCell align="right" sx={{ width: "140px" }}>Balance</TableCell>
									{!isRhpUser ? (
										<>
											<TableCell sx={{ width: "150px" }}>Sale Status</TableCell>
											<TableCell sx={{ width: "150px" }}>Payment Status</TableCell>
										</>
									) : null}
									{user_role !== '18' ? (<TableCell align="center" sx={{ width: "100px" }}>Action</TableCell>) : null}
								</TableRow>
							</TableHead>
							<TableBody >
								{isLoading ? (
									<TableRow>
										<TableCell colSpan={tableColSpan} sx={{ textAlign: "center", py: 8 }}>
											<Typography sx={{ color: "#637381", fontSize: "0.875rem" }}>
												Loading...
											</Typography>
										</TableCell>
									</TableRow>
								) : filteredOrders.length === 0 ? (
									<TableRow>
										<TableCell colSpan={tableColSpan} sx={{ textAlign: "center", py: 8 }}>
											<Typography sx={{ color: "#637381", fontSize: "0.875rem" }}>
												{isRhpUser ? 'No orders found' : 'No incomplete orders found'}
											</Typography>
										</TableCell>
									</TableRow>
								) : (
									filteredOrders.map((order, index) => (
										<TableRow
											key={order.ids || index}
											hover
											sx={{
												"&:hover": {
													backgroundColor: "rgba(145, 158, 171, 0.04)"
												},
												transition: "background-color 0.2s ease",
												"& .MuiTableCell-root": {

													fontSize: "0.75rem",
													color: "#212b36",
													py: 1,
												},

											}}
										>
											<TableCell>
												{order.date || ""}
											</TableCell>
											<TableCell sx={{ textDecoration: "underline" }}>
												<Link
													to={`${config.BASE_URL}/sales/invoice/${order.ids}`}
													style={{
														color: "#212b36",
														textDecoration: "underline",
														fontWeight: 500,
													}}
												>
													{order.reference_no || ""}
												</Link>
											</TableCell>
											<TableCell >
												{order.customer || ""}
											</TableCell>
											<TableCell align="right" >
												{formatCurrency(order.grand_total)}
											</TableCell>
											<TableCell align="right" >
												{formatCurrency(order.paid)}
											</TableCell>
											<TableCell align="right" >
												{formatCurrency(0)}
											</TableCell>
											{!isRhpUser ? (
												<>
													<TableCell >
														<Chip
															label={order.sale_status || "Incomplete"}
															size="small"
															sx={{
																backgroundColor: "#fff3e0",
																color: "#f57c00",
																fontWeight: 500,
																fontSize: "0.75rem",
																height: 24,
																'&:hover': {
																	backgroundColor: "#fff3e0",
																	color: "#f57c00",
																}
															}}

														/>
													</TableCell>
													<TableCell >
														<Chip
															label="Unpaid"
															size="small"
															sx={{
																backgroundColor: "#ffebee",
																color: "#d32f2f",
																fontWeight: 500,
																fontSize: "0.75rem",
																height: 24,
																'&:hover': {
																	backgroundColor: "#ffebee",
																	color: "#d32f2f",
																}
															}}
														/>
													</TableCell>
												</>
											) : null}
											{user_role !== '18' ? (
												<TableCell align="center" >
													<Button
														variant="contained"
														size="small"

														onClick={() => handlePayClick(order)}
														sx={{
															textTransform: "none",
															fontWeight: 500,
															fontSize: "0.7rem",
															boxShadow: "none",
															backgroundColor: "#F8991D",
															minWidth: 60,
															padding: "4px 8px",
															height: 26,
															'&:hover': {
																boxShadow: "none",
																backgroundColor: "#F8991D",
																color: "#fff"
															}
														}}
													>
														Pay
													</Button>
												</TableCell>
											) : null
											}
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>
					<Box pt={1} display="flex" justifyContent="flex-end" alignItems="center">
						<TablePaginationCustom
							count={totalRecords}
							page={table.page}
							rowsPerPage={table.rowsPerPage}
							onPageChange={table.onChangePage}
							onRowsPerPageChange={table.onChangeRowsPerPage}
							labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
						    className="pagination-styles"
						/>
					</Box>
				</Paper>
			</Box>
			<Dialog
				open={paymentDialogOpen}
				onClose={() => {
					setPaymentDialogOpen(false);
					clearPaymentData();
				}}
				maxWidth="md"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: 2,
					},
				}}
			>
				<DialogTitle className="page-title" sx={{ pb: 1 }}>
					Add Payment
				</DialogTitle>
				<DialogContent>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, py: 1 }}>
						<Box sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							p: 2,
							bgcolor: "#fafafa",
							borderRadius: 1,
							border: "1px solid rgba(40, 42, 44, 0.76)"
						}}>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								Invoice Total: {currencySymbol}{parseFloat(selectedOrder?.grand_total || 0).toFixed(2)}
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500, color: "#2e7d32" }}>
								Paid: {currencySymbol}{paidAmt.toFixed(2)}
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500, color: "#d32f2f" }}>
								Balance: {currencySymbol}{balance}
							</Typography>
						</Box>

						<TableContainer variant="outlined" sx={{ maxHeight: 300 }}>
							<Table size="small" className="table-cell-border">
								<TableHead>
									<TableRow className="table-cell-header">
										<TableCell sx={{ width: '10%' }}>No</TableCell>
										<TableCell align="right" sx={{ width: '20%', }}>Paid</TableCell>
										<TableCell align="right" sx={{ width: '20%', }}>Mode</TableCell>
										<TableCell >Details</TableCell>
										<TableCell sx={{ width: '10%' }}>Action</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{payments.length === 0 ? (
										<TableRow>
											<TableCell colSpan={5} align="center" sx={{ py: 3, color: "#637381" }}>
												No payments added yet
											</TableCell>
										</TableRow>
									) : (
										payments.map((payment, index) => (
											<TableRow key={payment.uid} hover>
												<TableCell>{index + 1}</TableCell>
												<TableCell align="right">{currencySymbol}{parseFloat(payment.amount).toFixed(2)}</TableCell>
												<TableCell align="right">
													{payment.method === 'creditNote'
														? 'Credit Note'
														: payment.method.charAt(0).toUpperCase() + payment.method.slice(1)}
												</TableCell>
												<TableCell>
													{payment.chequeNo
														|| payment.cardNumber
														|| payment.voucherNo
														|| '--'}
												</TableCell>
												<TableCell>
													<IconButton
														size="small"
														color="error"
														onClick={() => handleAmountCancel(payment.uid)}
													>
														<Iconify icon="eva:trash-2-outline" width={20} />
													</IconButton>
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</TableContainer>

						<Box sx={{ border: "1px solid rgba(145, 158, 171, 0.16)", borderRadius: 1, p: 1.5 }}>
							{activePaymentMethod !== 'creditNote' ? (
								<Stack
									direction="row"
									spacing={1}
									alignItems="center"
									sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}
								>
									<Typography variant="body2" sx={{ minWidth: 58, fontWeight: 500, fontSize: '0.75rem' }}>
										Amount
									</Typography>
									<TextField
										className="input-border"
										type="text"
										placeholder="0.00"
										value={amount}
										onChange={(e) => {
											const value = e.target.value.replace(/[^0-9.]/g, '');
											const finalValue = value.indexOf('.') !== -1
												? value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
												: value;
											setAmount(finalValue);
										}}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												e.preventDefault();
												addPaymentWithMethod(activePaymentMethod === 'creditNote' ? 'cash' : activePaymentMethod);
											}
										}}
										size="small"
										sx={{
											width: 120,
											"& .MuiOutlinedInput-root": {
												height: 32,
												fontSize: '0.75rem',
											},
										}}
									/>
									{activePaymentMethod === 'card' ? (
										<TextField
											className="input-border"
											type="text"
											placeholder="Transaction ID"
											value={cardNumber}
											onChange={(e) => setCardNumber(e.target.value)}
											size="small"
											sx={{
												width: 140,
												"& .MuiOutlinedInput-root": {
													height: 32,
													fontSize: '0.75rem',
												},
											}}
										/>
									) : null}
									{activePaymentMethod === 'cheque' ? (
										<>
											<TextField
												className="input-border"
												type="text"
												placeholder="Cheque No"
												value={chequeNo}
												onChange={(e) => setChequeNo(e.target.value)}
												size="small"
												sx={{
													width: 120,
													"& .MuiOutlinedInput-root": {
														height: 32,
														fontSize: '0.75rem',
													},
												}}
											/>
											<LocalizationProvider dateAdapter={AdapterDateFns}>
												<DatePicker
													label="Cheque Date"
													value={chequeDate ? new Date(chequeDate) : null}
													onChange={(newValue) => {
														setChequeDate(newValue ? newValue.toISOString().split('T')[0] : '');
													}}
													slotProps={{
														textField: {
															className: 'input-border',
															size: 'small',
															sx: {
																width: 150,
																"& .MuiOutlinedInput-root": {
																	height: 32,
																	fontSize: '0.75rem',
																},
															},
														},
													}}
												/>
											</LocalizationProvider>
										</>
									) : null}
									{activePaymentMethod === 'voucher' ? (
										<>
											<TextField
												className="input-border"
												type="text"
												placeholder="Voucher No"
												value={voucherNo}
												onChange={(e) => setVoucherNo(e.target.value)}
												size="small"
												sx={{
													width: 120,
													"& .MuiOutlinedInput-root": {
														height: 32,
														fontSize: '0.75rem',
													},
												}}
											/>
											<LocalizationProvider dateAdapter={AdapterDateFns}>
												<DatePicker
													label="Expiry Date"
													value={voucherExpiryDate ? new Date(voucherExpiryDate) : null}
													onChange={(newValue) => {
														setVoucherExpiryDate(newValue ? newValue.toISOString().split('T')[0] : '');
													}}
													slotProps={{
														textField: {
															className: 'input-border',
															size: 'small',
															sx: {
																width: 150,
																"& .MuiOutlinedInput-root": {
																	height: 32,
																	fontSize: '0.75rem',
																},
															},
														},
													}}
												/>
											</LocalizationProvider>
										</>
									) : null}
								</Stack>
							) : null}

							{activePaymentMethod === 'creditNote' ? (
								<Card variant="outlined" sx={{ mb: 1.5 }}>
									<CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
										{selectedOrder?.credit_type_name && selectedOrder?.credit_type_name !== 'undefined' ? (
											<Stack spacing={1}>
												<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
													<strong>Credit Type:</strong> {selectedOrder.credit_type_name}
												</Typography>
												<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
													<strong>C. Bal:</strong> {currencySymbol}{(parseFloat(selectedOrder.current_bal) || 0).toFixed(2)}
												</Typography>
												<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
													<strong>Amt. Due:</strong> {currencySymbol}{(parseFloat(selectedOrder.prev_due) || 0).toFixed(2)}
												</Typography>
											</Stack>
										) : (
											<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
												<strong>No Credit Facility</strong>
											</Typography>
										)}
									</CardContent>
								</Card>
							) : null}

							<Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
								{[
									{ key: 'cash', label: 'Cash' },
									{ key: 'card', label: 'Card' },
									{ key: 'creditNote', label: 'Credit Note' },
									{ key: 'cheque', label: 'Cheque' },
									{ key: 'voucher', label: 'Voucher' },
								].map((method) => (
									<Button
										key={method.key}
										variant={activePaymentMethod === method.key ? 'contained' : 'outlined'}
										onClick={() => addPaymentWithMethod(method.key)}
										size="small"
										sx={{
											textTransform: 'none',
											fontWeight: 500,
											fontSize: '0.75rem',
											minWidth: 72,
											height: 30,
											px: 1.25,
											...(activePaymentMethod === method.key && {
												backgroundColor: '#F8991D',
												borderColor: '#F8991D',
												'&:hover': { backgroundColor: '#e08a15', borderColor: '#e08a15' }
											})
										}}
									>
										{method.label}
									</Button>
								))}
							</Stack>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
					<Button
						onClick={() => {
							setPaymentDialogOpen(false);
							clearPaymentData();
						}}
						className="button-variant-outlined"
						variant="outlined"
					>
						Cancel
					</Button>
					<Button
						onClick={handlePaymentSubmit}
						variant="contained"
						className="button-variant-contained"
						disabled={
							isPaymentSubmitting ||
							(activePaymentMethod === 'creditNote' && (!selectedOrder?.credit_type_name || selectedOrder?.credit_type_name === 'undefined')) ||
							(activePaymentMethod !== 'creditNote' && paidAmt < parseFloat(selectedOrder?.grand_total || 0))
						}
						sx={{
							opacity: (isPaymentSubmitting ||
								(activePaymentMethod === 'creditNote' && (!selectedOrder?.credit_type_name || selectedOrder?.credit_type_name === 'undefined')) ||
								(activePaymentMethod !== 'creditNote' && paidAmt < parseFloat(selectedOrder?.grand_total || 0))) ? 0.5 : 1,
							cursor: (isPaymentSubmitting ||
								(activePaymentMethod === 'creditNote' && (!selectedOrder?.credit_type_name || selectedOrder?.credit_type_name === 'undefined')) ||
								(activePaymentMethod !== 'creditNote' && paidAmt < parseFloat(selectedOrder?.grand_total || 0))) ? 'not-allowed' : 'pointer',
						}}
					>
						{isPaymentSubmitting ? 'Saving…' : 'Save & Print'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default IncompleteOrderPosList;
